import { atom } from "jotai";
import { atomWithStorage, createJSONStorage } from "jotai/utils";
import { ToastMessageInterface } from "@app/interfaces/ToastMessageInterface";
import { ThemeEnum } from "@app/models/ThemeEnum";
import { ModelCardInterface } from "@dashart/dashart-gpt-shared-library";
import UserInterface from "@dashart/dashart-gpt-shared-library/dist/interfaces/UserInterface";

// The app previously used recoil + recoil-persist, which stored all persisted
// atoms as one object under the 'recoil-persist' localStorage key. These
// atoms keep writing to that same key/format so existing users keep their
// persisted theme, selected models etc. across the migration.
const LEGACY_RECOIL_PERSIST_KEY = 'recoil-persist';

const legacyRecoilPersistStorage = () => ({
    getItem: (key: string): string | null => {
        try {
            const raw = localStorage.getItem(LEGACY_RECOIL_PERSIST_KEY);
            if (raw === null) {
                return null;
            }
            const data = JSON.parse(raw);
            return data?.[key] === undefined ? null : JSON.stringify(data[key]);
        } catch {
            return null;
        }
    },
    setItem: (key: string, value: string): void => {
        try {
            const raw = localStorage.getItem(LEGACY_RECOIL_PERSIST_KEY);
            const data = raw ? JSON.parse(raw) : {};
            data[key] = JSON.parse(value);
            localStorage.setItem(LEGACY_RECOIL_PERSIST_KEY, JSON.stringify(data));
        } catch {
            // ignore storage errors (quota, disabled storage etc.)
        }
    },
    removeItem: (key: string): void => {
        try {
            const raw = localStorage.getItem(LEGACY_RECOIL_PERSIST_KEY);
            if (raw === null) {
                return;
            }
            const data = JSON.parse(raw);
            delete data[key];
            localStorage.setItem(LEGACY_RECOIL_PERSIST_KEY, JSON.stringify(data));
        } catch {
            // ignore storage errors
        }
    },
});

const persistedAtom = <Value>(key: string, initialValue: Value) =>
    atomWithStorage(key, initialValue, createJSONStorage<Value>(legacyRecoilPersistStorage), {
        getOnInit: true,
    });

export const currentUserAtom = atom<UserInterface>(null as unknown as UserInterface);
export const availableModelsAtom = atom<ModelCardInterface[]>([]);
// Not persisted: models are fetched fresh on mount per service.
// Persisting caused stale models from the last-used provider to show
// for all other providers until their fetch completed (or across restarts).
export const selectedModelsByServiceNamesAtom = persistedAtom<{[key:string]: ModelCardInterface}>('selectedModelState', {});
export const lastToastMessageAtom = atom<ToastMessageInterface>(null as unknown as ToastMessageInterface);
export const lastUsedConversationIdAtom = atom<string>(null as unknown as string);
export const chatHistoryOpenAtom = persistedAtom<boolean>('chatHistoryOpenState', true);
export const instructionsSelectorDialogTabIndexAtom = persistedAtom<number>('instructionsSelectorDialogTabIndexState', 0);
export const lastUpdatedInstructionIdAndTsAtom = persistedAtom<string | null>('lastUpdatedInstructionIdAndTsState', null);
export const themeAtom = persistedAtom<ThemeEnum>('themeState', ThemeEnum.LIGHT);
